import React, { useEffect, useRef, useState } from 'react'
import * as R from 'ramda'
import { usePlace } from 'commons/usePlace'
import Assets from 'app/configs/assetConfigs'
import UploadEditableAvatar from 'app/components/widgets/UploadEditableAvatar'
import { megaBytesToBytes, fileToBase64, alertSizeOverLimit } from 'libs/file'
import MessageAlert from 'app/components/MessageAlert'
import {
  updatePlaceContactDetail,
  setPlace,
} from 'app/store/actions/place.actions'
import effects from 'app/effects/effects'
import { useDispatch } from 'react-redux'
import { FormattedMessage, useIntl } from 'react-intl'
import { TextInput } from '../../../components/FormsyHelperComponent'
import useCategory from 'app/components/categories/useCategory'
import AutocompleteSelectOptionGrouped from 'app/components/AutocompleteSelectOptionGrouped'
import Formsy from 'formsy-react'
import { useFormsy } from 'commons/form'
import EmailInputFormsy, {
  EmailInputValidationType,
} from '../../../components/EmailInputFormsy'
import PhoneNumberInputFormsy, {
  validatePhoneNumber,
} from '../../../components/PhoneNumberInputFormsy'
import { PlacesAddressAutocompleteFormsy } from 'app/components/PlacesAutocompleteFormsy'
import { TextFieldFormsy } from '@fuse'
import { getFullPhoneNumber, parsedPhoneNumber } from 'commons/phoneNumber'
import { websiteRegex } from 'commons/website'
import {
  MAX_DESCRIPTION_TEXT_LENGTH,
  pruneValues,
  updatedValue,
} from 'commons/placeInfoForm'
import PrimaryButton from '../../../components/buttons/PrimaryButton'
import CompleteYourPlaceProfile from '../../../components/CompleteYourPlaceProfile'
import Map from 'app/components/Map'
import { mapLocation } from 'app/main/profile/components/ProfileViewMapper'
import { Checkbox, FormControlLabel, Grid } from '@material-ui/core'

import { WarningVerificationEmail } from '../../../components/WarningVerificationEmail'
import ConfirmationDialog from 'app/components/ConfirmationDialog'
import DestructiveButton from 'app/components/buttons/DestructiveButton'
import SecondaryButton from 'app/components/buttons/SecondaryButton'
import { makeStyles } from '@material-ui/styles'
import clsx from 'clsx'
import { validateGsm } from 'commons/validateGsm'

const PanelWrapper = (content) => {
  return <div className="mb-16">{content}</div>
}

const useStyles = makeStyles(() => ({
  formRow: {
    '@media (max-width: 780px)': {
      flexDirection: 'column',
      '& .ml-20': {
        marginLeft: '0 !important',
        marginTop: '3.2rem',
      },
    },
  },
  placeDetailsContainer: {
    '@media (max-width: 780px)': {
      flexDirection: 'column',
      alignItems: 'center',
    },
    '& .ressponsiveLogo': {
      marginBottom: '20px',
    },
  },
}))

const MAX_SIZE_FOR_POPUP = 100
const MAX_PLACE_NAME_FOR_SMS = 38

export default () => {
  const PHOTO_SIZE_LIMIT = megaBytesToBytes(10)

  const place = usePlace()
  const dispatch = useDispatch()
  const { formatMessage } = useIntl()
  const [isPhotoUploading, setIsPhotoUploading] = useState(false)
  const [isMapLoaded, setIsMapLoaded] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isTranslationEnabled, setIsTranslationEnabled] = useState(
    R.view(R.lensPath(['info', 'isTranslationEnabled']), place),
  )
  const location = {
    latitude: R.view(R.lensPath(['info', 'latitude']), place),
    longitude: R.view(R.lensPath(['info', 'longitude']), place),
  }
  const [latLng, setLatLng] = useState({
    lat: location.latitude,
    lng: location.longitude,
  })

  const [timezone, setTimezone] = useState(
    R.view(R.lensPath(['info', 'timezone']), place),
  )

  const { categories } = useCategory()

  const placeName = R.prop('name', place)
  const countryCode = R.view(R.lensPath(['info', 'countryCode']), place)
  const phoneNumber = R.view(R.lensPath(['info', 'phoneNumber']), place)
  const address = R.view(R.lensPath(['info', 'address']), place)
  const fullPhoneNumber = getFullPhoneNumber(countryCode, phoneNumber)
  const contactUrl = R.view(R.lensPath(['info', 'contactUrl']), place)
  const shortName = R.view(R.lensPath(['info', 'shortName']), place)
  const description = R.view(R.lensPath(['info', 'description']), place)
  const [center, setCenter] = useState(mapLocation(place))
  const email = R.prop('email', place)
  const category = R.view(R.lensPath(['info', 'category']), place)
  const [categorySelected, setCategorySelected] = useState(category)
  const [isThenDescriptionValue, setIsThenDescriptionValue] = useState(
    description ? description.length < MAX_SIZE_FOR_POPUP : true,
  )

  const formRef = useRef(null)
  const { isPristine, onChange } = useFormsy(formRef)

  const handleSelectPlaceSuggestion = (values) => {
    setLatLng({ lat: values.lat, lng: values.lng })
    setCenter({ lat: values.lat, lng: values.lng })
    setTimezone(values.timeZoneId)
  }
  const onTranslationChange = (e) => {
    const value = e.target.checked === true ? 1 : 0
    setIsTranslationEnabled(value)
  }
  const handleClearLocation = () => {
    setLatLng({ lat: null, lng: null })
  }

  useEffect(() => {
    if (!R.isEmpty(categories)) {
      setCategorySelected(R.find(R.propEq('id', category.id))(categories))
    }
  }, [category, categories, setCategorySelected])

  const handleUploadAvatar = R.ifElse(
    R.propSatisfies(R.lt(R.__, PHOTO_SIZE_LIMIT), 'size'),
    (file) => {
      setIsPhotoUploading(true)
      return fileToBase64(file)
        .then((fileData) => {
          effects.apis.uploadProfilePhoto({
            // placeId: place.id,
            data: { file: fileData },
          })
        })
        .then(() => effects.apis.getPlaceByIdAuth(place.id))
        .then((place) => {
          dispatch(setPlace(place))
          setIsPhotoUploading(false)
        })
        .catch((error) => {
          dispatch(MessageAlert.showNetworkError(formatMessage)(error))
          setIsPhotoUploading(false)
        })
    },
    () => {
      alertSizeOverLimit(dispatch, formatMessage, PHOTO_SIZE_LIMIT)
    },
  )

  const [isFormValid, setIsFormValid] = useState(false)

  const disableButton = () => {
    setIsFormValid(false)
  }

  const enableButton = () => {
    setIsFormValid(true)
  }

  const updateProfile = async (model) => {
    const updatedPhoneNumber =
      updatedValue({ countryCode, phoneNumber })(
        parsedPhoneNumber(model.fullPhoneNumber),
      ) || {}
    setIsSubmitting(true)
    effects.apis
      .saveProfileInfo({
        placeId: place.id,
        data: pruneValues({
          name: updatedValue(placeName)(model.placeName),
          categoryId: updatedValue(category.id)(categorySelected.id),
          email: updatedValue(email)(model.email),
          address: updatedValue(address)(model.address),
          latitude: latLng.lat,
          longitude: latLng.lng,
          contactUrl: updatedValue(contactUrl)(model.contactUrl),
          description: model.description,
          isTranslationEnabled: updatedValue(
            R.view(R.lensPath(['info', 'isTranslationEnabled']), place) ===
              isTranslationEnabled,
          )(isTranslationEnabled),
          ...updatedPhoneNumber,
          shortName: model.shortName,
          timezone,
        }),
      })
      .then(() => effects.apis.getPlaceByIdAuth(place.id))
      .then((newPlace) => {
        dispatch(setPlace(newPlace))
        dispatch(
          MessageAlert.showSuccess({
            message: formatMessage({
              id: 'profile.info.success',
            }),
          }),
        )
      })
      .catch((error) => {
        function makeErrorMessage(error) {
          if (
            !R.isNil(
              R.path(
                [
                  'response',
                  'data',
                  'details',
                  'message',
                  'errors',
                  'shortName',
                ],
                error,
              ),
            )
          ) {
            return {
              alert: formatMessage({ id: 'error.400.invalidplaceshortname' }),
              inputErrors: {
                shortName: formatMessage({
                  id: 'error.400.invalidPlaceShortName',
                }),
              },
            }
          }
          return {
            alert: formatMessage(
              { id: 'error.general.subTitle' },
              { newline: <br /> },
            ),
          }
        }
        const errorMessage = makeErrorMessage(error)
        if (errorMessage.inputErrors)
          formRef.current.updateInputsWithError(errorMessage.inputErrors)
        dispatch(
          MessageAlert.showError({
            message: errorMessage.alert,
          }),
        )
      })
      .finally(() => {
        setIsSubmitting(false)
      })
  }

  const [isShowConfirmDialog, setIsShowConfirmDialog] = useState(false)

  const handleResentPlaceVerificationEmail = (pendingEmail) => async () => {
    try {
      await effects.apis.resentPlaceVerificationEmail()
      dispatch(
        MessageAlert.showSuccess({
          message: formatMessage(
            { id: 'profile.contactDetail.success.email.verification' },
            {
              pendingEmail,
              b: (text) => (
                <span
                  key="profile.contactDetail.success.email.verification"
                  className="font-bold ltr:ml-2 rtl:mr-2"
                >
                  {text}
                </span>
              ),
            },
          ),
        }),
      )
    } catch (error) {
      const errorCode = error.details?.code
      if (Number(errorCode) === 650) {
        dispatch(
          MessageAlert.showError({
            message: formatMessage({ id: 'error.650.newEmailNotexist' }),
          }),
        )
      } else {
        dispatch(MessageAlert.showNetworkError(formatMessage)(error))
      }
    } finally {
      setIsShowConfirmDialog(false)
    }
  }

  const handleCancelPlaceVerificationEmail = async () => {
    try {
      await effects.apis.cancelPlaceVerificationEmail()
      dispatch(updatePlaceContactDetail({ newEmail: null }))
    } catch (error) {
      dispatch(MessageAlert.showNetworkError(formatMessage)(error))
    } finally {
      setIsShowConfirmDialog(false)
    }
  }

  const listenDescriptionLength = (description) => {
    setIsThenDescriptionValue(description < MAX_SIZE_FOR_POPUP)
  }
  const classes = useStyles()
  return (
    <div>
      {isThenDescriptionValue &&
        PanelWrapper(
          <CompleteYourPlaceProfile
            isThenDescriptionValue={isThenDescriptionValue}
          />,
        )}
      <Formsy
        onChange={onChange}
        onValid={enableButton}
        onInvalid={disableButton}
        ref={formRef}
        onSubmit={updateProfile}
        className="flex flex-col overflow-hidden"
      >
        <div
          className={clsx(
            'flex justify-center mb-20 pt-5',
            classes.placeDetailsContainer,
          )}
        >
          <UploadEditableAvatar
            name="logo"
            onChange={handleUploadAvatar}
            src={R.view(R.lensPath(['info', 'logo']), place)}
            isLoading={isPhotoUploading}
            fallbackSrc={Assets.defaultPlaceImage}
            className="mr-28 ressponsiveLogo"
          />
          <div className="w-full">
            <div
              className={clsx('flex justify-between w-full', classes.formRow)}
            >
              <div className="w-full">
                <TextInput
                  required={true}
                  listenDescriptionLength={listenDescriptionLength}
                  containerClassName="w-full"
                  labelId="profile.description.label"
                  placeholderId="profile.description.label"
                  value={description || ''}
                  name="description"
                  variant="outlined"
                  multiline
                  rows={5}
                  fullWidth
                  data-testid="profileEditPlaceDescription"
                  maxLength={MAX_DESCRIPTION_TEXT_LENGTH}
                  validations={{
                    minLength: 100,
                  }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      disableRipple
                      checked={isTranslationEnabled === 1}
                      color="default"
                      onChange={(e) => onTranslationChange(e)}
                      inputProps={{
                        'aria-label': 'Translate Description',
                        'data-testid': 'translateDescription',
                      }}
                    />
                  }
                  label={formatMessage({
                    id: 'translate.description',
                  })}
                />
              </div>
            </div>
          </div>
        </div>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={6} lg={6}>
            <TextInput
              containerClassName="w-full"
              labelId="profile.editingPlaceNameDialog.textField.label"
              placeholderId="profile.editingPlaceNameDialog.textField.placeHolder"
              value={placeName || ''}
              name="placeName"
              variant="outlined"
              autoFocus
              fullWidth
              maxLength="100"
              required
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={6}>
            <AutocompleteSelectOptionGrouped
              name="category"
              onSelected={setCategorySelected}
              options={categories}
              initialValue={categorySelected}
              label={formatMessage({
                id: R.isNil(categorySelected)
                  ? 'profileInfo.editCategory.dialog.empty.data'
                  : 'profileInfo.editCategory.dialog.active.data',
              })}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={6}>
            <EmailInputFormsy
              labelId="email.address.label"
              validations={{
                isEmail: true,
              }}
              validationErrors={{
                isEmail: formatMessage({
                  id: 'profile.email.formatInvalid.label',
                }),
              }}
              value={email || ''}
              name="email"
              variant="outlined"
              inputProps={{
                'data-testid': 'profileEditEmail',
              }}
              fullWidth
              validationType={EmailInputValidationType.official}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={6}>
            <PhoneNumberInputFormsy
              name="fullPhoneNumber"
              value={fullPhoneNumber || ''}
              label={formatMessage({ id: 'profile.phoneNumber.label' })}
              autoComplete="new-password"
              enableSearchField
              disableSearchIcon
              disableAreaCodes={true}
              defaultCountry={countryCode}
              validations={{
                isPhoneNumber: validatePhoneNumber,
              }}
              validationErrors={{
                isPhoneNumber: formatMessage({
                  id: 'registerForm.phoneNumberInvalid',
                }),
              }}
              InputProps={{
                'data-testid': 'profileEditPhoneNo',
              }}
              required
              isScrollWhenFocus
            />
          </Grid>
          {R.prop('newEmail', place) && (
            <Grid item xs={12} sm={12} md={12} lg={12}>
              <WarningVerificationEmail
                pendingEmail={R.prop('newEmail', place)}
                onResentPlaceVerificationEmail={handleResentPlaceVerificationEmail(
                  R.prop('newEmail', place),
                )}
                onCancelPlaceVerificationEmail={() =>
                  setIsShowConfirmDialog(true)
                }
              />
            </Grid>
          )}
          <Grid item xs={12} sm={12} md={6} lg={6}>
            <TextFieldFormsy
              label={formatMessage({ id: 'profile.contactUrl.label' })}
              validations={{
                matchRegexp: websiteRegex,
              }}
              validationErrors={{
                matchRegexp: formatMessage({
                  id: 'profile.contactUrl.formatInvalid.label',
                }),
              }}
              value={contactUrl || ''}
              name="contactUrl"
              variant="outlined"
              inputProps={{
                'data-testid': 'profileEditContactUrl',
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={6}>
            <TextInput
              containerClassName="w-full"
              labelId="profile.info.placeSms.textfield.label"
              placeholderId="profile.info.placeSms.textfield.label"
              value={shortName || ''}
              name="shortName"
              variant="outlined"
              fullWidth
              maxLength={MAX_PLACE_NAME_FOR_SMS}
              validations={{
                isGsm: validateGsm,
              }}
              validationErrors={{
                isGsm: formatMessage({
                  id: 'profile.info.placeSms.textfield.errorGsm.text',
                }),
              }}
            />
          </Grid>
          <Grid item xs={12} md={12} lg={12}>
            {isMapLoaded && (
              <PlacesAddressAutocompleteFormsy
                value={address || ''}
                name="address"
                isDisabled={true}
                labelId="profile.address.label"
                placeholderId="profile.address.label"
                onSelectPlaceSuggestion={handleSelectPlaceSuggestion}
                handleClearLocation={handleClearLocation}
                InputProps={{
                  multiline: true,
                  'data-testid': 'profileEditAdress',
                }}
                isMapLoaded={isMapLoaded}
              />
            )}
          </Grid>
          <Grid item xs={12} md={12} lg={12}>
            <div className="relative h-full w-full" style={{ minHeight: 260 }}>
              <Map
                center={center}
                gestureHandling="none"
                onReady={(_, map) => {
                  setIsMapLoaded(true)
                  map.setOptions({
                    draggableCursor: 'default',
                    draggingCursor: 'pointer',
                  })
                }}
              />
            </div>
          </Grid>
        </Grid>
        <div className="w-96 ml-auto mt-20">
          <PrimaryButton
            className="w-full"
            type="submit"
            aria-label="save"
            isLoading={isSubmitting}
            disabled={
              description === '' ||
              !categorySelected ||
              !isFormValid ||
              (isPristine &&
                categorySelected.id === category.id &&
                R.view(R.lensPath(['info', 'isTranslationEnabled']), place) ===
                  isTranslationEnabled)
            }
            labelId="action.save"
          />
        </div>
      </Formsy>
      <ConfirmationDialog
        open={isShowConfirmDialog}
        title={formatMessage({
          id: 'profile.contactDetail.confirmDialog.email.verification.title',
        })}
        body={formatMessage({
          id: 'profile.contactDetail.confirmDialog.email.verification.body',
        })}
        CancelButton={
          <SecondaryButton onClick={() => setIsShowConfirmDialog(false)}>
            <FormattedMessage id="profile.contactDetail.confirmDialog.email.verification.cancelButton" />
          </SecondaryButton>
        }
        ConfirmButton={
          <DestructiveButton onClick={handleCancelPlaceVerificationEmail}>
            <FormattedMessage
              className="uppercase"
              id="profile.contactDetail.confirmDialog.email.verification.okButton"
            />
          </DestructiveButton>
        }
      />
    </div>
  )
}
